import { FolderOpen, Loader2, Plus } from 'lucide-react'
import type { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import type { Case } from '@/services/api'
import { CaseCard } from './CaseCard'

export interface CasesGridViewProps {
  cases: Case[]
  loading: boolean
  /** Whether a search filter is active (drives the empty-state copy). */
  hasSearch: boolean
  onOpen: (caseId: string) => void
  onDelete: (caseData: Case) => void
  onCreateFirst: () => void
}

/**
 * Pure presentation for the cases dashboard grid. The connected
 * {@link import('@/features/cases/CasesDashboard').CasesDashboard} supplies
 * `cases`/`loading` from the case-list query and wires the callbacks.
 */
export function CasesGridView({
  cases,
  loading,
  hasSearch,
  onOpen,
  onDelete,
  onCreateFirst,
}: CasesGridViewProps) {
  const { t } = useTranslation()

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (cases.length === 0) {
    return (
      <Card className="bg-card/30 border-border border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            {hasSearch ? t('cases.dashboard.noCasesFound') : t('cases.dashboard.noCasesYet')}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {hasSearch
              ? t('cases.dashboard.tryDifferentSearch')
              : t('cases.dashboard.createFirstHint')}
          </p>
          {!hasSearch && (
            <Button
              onClick={onCreateFirst}
              variant="outline"
              className="border-border/50 text-foreground hover:bg-accent"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('cases.dashboard.createFirstBtn')}
            </Button>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {cases.map(c => (
        <CaseCard
          key={c.id}
          caseData={c}
          onOpen={() => onOpen(c.id)}
          onDelete={(e: MouseEvent) => {
            e.stopPropagation()
            onDelete(c)
          }}
        />
      ))}
    </div>
  )
}
